import * as React from 'react';
import { useState } from 'react';

import analytics from '@repo/analytics';
import { DOWNLOAD_SAP } from '@repo/editor/src/global/events';
import { withEventScope } from '@repo/event-processor';
import {
  Button,
  primaryTheme,
  secondaryTheme,
} from '@repo/ui/components/Buttons';
import DeleteIcon from '@repo/ui/components/Icon/DeleteIcon';
import DownloadIcon from '@repo/ui/components/Icon/DownloadIcon';
import { fireToast } from '@repo/ui/components/Toast';
import { H3, H5, P2 } from '@repo/ui/components/Typography';
import { Modal } from '@repo/ui/deprecated/Modal';
import { themeStyle } from '@repo/ui/theming';
import { format } from 'date-fns';

import {
  ImagePreview,
  ImageWrapper,
  ModalWrapper,
  Prompt,
  PromptText,
  PromptWrapper,
} from 'components/BackgroundReplacer/BackgroundReplacerTool/styles';
import { GeneratedImage } from 'components/BackgroundReplacer/Gallery/types';
import SapDownloadPayload from 'global/types/Analytics';
import { getImageDimensions } from 'global/utils/fileUtils';
import useWindowSize from 'global/utils/useWindowSize';
import { useBackgroundReplacerStore } from 'stores/backgroundReplacerStore/backgroundReplacerStore';
import { hasItemsAndPages } from 'stores/backgroundReplacerStore/types';
import { FlexColumn, FlexRow } from 'styles/layouts';

import { downloadFile } from '../../AiTools/utils';

interface PreviewModalProps {
  item: GeneratedImage;
  isOpen: boolean;
  onClose: () => void;
}

const PreviewModal: React.FC<PreviewModalProps> = ({
  item,
  isOpen,
  onClose,
}) => {
  const { isMobile } = useWindowSize();

  const [deleting, setDeleting] = useState(false);

  const { state, tryDeleteImage } = useBackgroundReplacerStore();

  if (!hasItemsAndPages(state)) {
    return null;
  }

  const label = item?.label ?? 'Custom';

  const handleDownload = async (): Promise<void> => {
    if (deleting) {
      return;
    }

    try {
      await downloadFile(item.src!, label);
      const dimensions = await getImageDimensions(item.src!);
      const options: SapDownloadPayload = {
        page: 'background-generator',
        label: 'jpeg',
        dimensions: dimensions,
        removeBackground: true,
      };
      void analytics.track(DOWNLOAD_SAP, options);
    } catch (e) {
      fireToast({ label: 'Failed to download image', error: true });
      console.error(e);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (deleting) {
      return;
    }

    setDeleting(true);
    const deleted = await tryDeleteImage(item.id);
    if (deleted) {
      onClose();
    }
    setDeleting(false);
  };

  return (
    <Modal
      enableBackgroundScrolling={false}
      externalCloseButton={!isMobile}
      height={isMobile ? 'unset' : '100%'}
      isOpen={isOpen}
      stretchContent={!isMobile}
      theme={{ padding: '40px 10px' }}
      width={isMobile ? '100%' : 'unset'}
      onClose={onClose}
    >
      <ModalWrapper>
        <FlexRow justifyContent="space-between">
          <H3>{label}</H3>
          <P2 color={themeStyle.varInkMedium}>
            {!!item?.createdAt &&
              format(new Date(item.createdAt), 'MMM dd, yyyy')}
          </P2>
        </FlexRow>
        <ImageWrapper hasPrompt={!!item?.prompt}>
          <ImagePreview alt={item?.label} src={item?.src} />
        </ImageWrapper>
        <FlexRow gap="16px" justifyContent="space-between">
          <Button
            ariaLabel="Delete"
            height="40px"
            icon={{ component: DeleteIcon, height: '16px' }}
            loading={deleting}
            name="previewModalDeleteButton"
            theme={secondaryTheme}
            width="40px"
            onClick={handleDelete}
          />
          <Button
            disabled={deleting}
            height="40px"
            icon={{ component: DownloadIcon, height: '16px' }}
            label="Download"
            name="previewModalDownloadButton"
            theme={primaryTheme}
            width="126px"
            onClick={handleDownload}
          />
        </FlexRow>
        {item?.prompt && (
          <div style={{ position: 'relative' }}>
            <PromptWrapper>
              <Prompt>
                <FlexColumn gap="8px">
                  <H5 styles={{ fontWeight: themeStyle.fontSemiBold }}>
                    Include
                  </H5>
                  <PromptText color={themeStyle.varInkMedium}>
                    {item.prompt}
                  </PromptText>
                  {item.negativePrompt && (
                    <>
                      <H5 styles={{ fontWeight: themeStyle.fontSemiBold }}>
                        Do not include
                      </H5>
                      <PromptText color={themeStyle.varInkMedium}>
                        {item.negativePrompt}
                      </PromptText>
                    </>
                  )}
                </FlexColumn>
              </Prompt>
            </PromptWrapper>
          </div>
        )}
      </ModalWrapper>
    </Modal>
  );
};

export default withEventScope(PreviewModal, { module: 'previewModal' });
